import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreTradeRoute } from '../../../../containers/Routes/routes';
import { useAppLayoutId } from '../../../../hooks/useAppLayoutId';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';
import { clearActiveSession, selectActiveSessionId } from '../../redux/preTradeSlice';

/**
 * Navigates to a pre-trade history session, prompting for confirmation first if
 * doing so would discard the active session.
 */
export function useHistoryNavigation() {
  const dispatch = useAppStateDispatch();
  const navigate = useNavigate();
  const layoutId = useAppLayoutId('trading');
  const activeSessionId = useAppStateSelector(selectActiveSessionId);

  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  const navigateToSession = useCallback(
    (sessionId: string) => {
      // Opening a history session discards any active session — the two cannot coexist in the
      // current model
      dispatch(clearActiveSession());
      navigate(getPreTradeRoute({ layoutId, sessionId, tab: 'scenarios' }));
    },
    [dispatch, layoutId, navigate]
  );

  const handleOpenSession = useCallback(
    (sessionId: string) => {
      // Warn the user before discarding their active session — store the target and let the
      // caller render a confirmation dialog.
      if (activeSessionId) {
        setPendingSessionId(sessionId);
        return;
      }
      navigateToSession(sessionId);
    },
    [activeSessionId, navigateToSession]
  );

  const handleConfirmOpen = useCallback(() => {
    if (pendingSessionId) {
      navigateToSession(pendingSessionId);
      setPendingSessionId(null);
    }
  }, [navigateToSession, pendingSessionId]);

  return { handleOpenSession, pendingSessionId, setPendingSessionId, handleConfirmOpen };
}
